<template>
  <iColumn
    width="fill"
    gap="none"
    data-test="message-composer"
    :style-overrides="boxStyleOverrides"
    @click="handleEditorFocus"
  >
    <iMessageComposer
      :key="draftKey"
      ref="composer"
      :on-submit="sendSegments"
      :upload-file="uploadComposerFile"
      :completions="completions"
      :initial-segments="draftSegments"
      :on-user-input="rememberDraft"
    >
      <iMessageComposerSuggestions label="People you can mention" />
      <iMessageComposerAttachments label="Attachments to send" remove-label="Remove attachment" />
      <iMessageComposerFormatBar v-if="showsFormatting" label="Formatting" />
      <iRow
        width="fill"
        gap="small"
        vertical-align="middle"
        wrap="nowrap"
      >
        <iMessageComposerAttachButton />
        <iMessageComposerInput placeholder="Type a message..." autofocus :style-overrides="inputStyleOverrides" />
        <iButton
          variant="text"
          :aria-pressed="String(showsFormatting)"
          aria-label="Show formatting"
          data-test="message-composer-formatting-toggle"
          @mousedown.prevent
          @click="showsFormatting = !showsFormatting"
        >
          Aa
        </iButton>
        <iMessageComposerSendButton label="Send" icon="material-symbols:send-rounded" variant="text" />
      </iRow>
    </iMessageComposer>
  </iColumn>
</template>

<script>
import { mapActions, mapState, mapWritableState } from "pinia";
import { emojiCompletion } from "@bloglovin/vue-component-library";
import { useConversationStore } from "%/apps/conversations/stores/conversation-store";
import { useMessageStore } from "%/apps/conversations/stores/message-store";
import { useChannelStore } from "~/stores/channel-store";

const MENTION_LIMIT = 8;
const INPUT_STYLE_OVERRIDES = Object.freeze({
  backgroundColor: "none",
  borderColor: "none",
  focusBorderColor: "none",
  paddingHorizontal: "none",
});
const BOX_STYLE_OVERRIDES = Object.freeze({
  backgroundColor: "pure",
  borderColor: "light",
  borderWidth: "thin",
  borderRadius: "large",
  paddingTop: "small",
  paddingBottom: "small",
  paddingLeft: "standard",
  paddingRight: "standard",
});

function draftTextSegments(segments) {
  return segments.filter(segment => segment.segment_type === "rich_text");
}

export default {
  name: "CreateMessage",
  props: {
    context: {
      type: String,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
    parentContext: {
      type: String,
      required: false,
      default: "",
    },
    parentEntityId: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  emits: ["editor:focus", "message-sent"],
  data() {
    return {
      showsFormatting: false,
      inputStyleOverrides: INPUT_STYLE_OVERRIDES,
      boxStyleOverrides: BOX_STYLE_OVERRIDES,
    };
  },
  computed: {
    ...mapState(useChannelStore, ["activeEntityId"]),
    ...mapState(useConversationStore, ["conversationParticipants"]),
    ...mapWritableState(useConversationStore, ["draftMessages"]),
    draftContext() {
      return this.context || this.parentContext;
    },
    draftEntityId() {
      return this.entityId || this.parentEntityId;
    },
    draftKey() {
      return `${this.draftContext}:${this.draftEntityId}`;
    },
    draftSegments() {
      const draft = this.draftMessages[this.draftContext]?.[this.draftEntityId];
      return draft ? draftTextSegments(JSON.parse(draft)) : null;
    },
    completions() {
      return [
        {
          trigger: "@",
          insert: "mention",
          source: query => Promise.resolve(this.mentionItems(query)),
        },
        emojiCompletion,
      ];
    },
  },
  watch: {
    activeEntityId(newValue) {
      if (newValue) {
        this.focusEditor();
      }
    },
  },
  methods: {
    ...mapActions(useConversationStore, ["sendMessage"]),
    ...mapActions(useMessageStore, ["replyMessage", "uploadComposerFile"]),
    mentionItems(query) {
      const wanted = query.toLowerCase().trim();
      return this.conversationParticipants
          .filter(participant => {
            const title = participant.title?.toLowerCase() ?? "";
            const subtitle = participant.subTitle?.toLowerCase() ?? "";
            return !wanted || title.includes(wanted) || subtitle.includes(wanted);
          })
          .slice(0, MENTION_LIMIT)
          .map(participant => ({
            id: participant.entityId,
            label: String(participant.title ?? "").replaceAll(" ", ""),
            avatarUrl: participant.avatar || undefined,
          }));
    },
    saveDraft(message) {
      this.draftMessages = {
        ...this.draftMessages,
        [this.draftContext]: {
          ...this.draftMessages[this.draftContext],
          [this.draftEntityId]: message,
        },
      };
    },
    rememberDraft() {
      this.$nextTick(() => {
        const segments = this.$refs.composer?.payload();
        if (segments) {
          this.saveDraft(JSON.stringify(draftTextSegments(segments)));
        }
      });
    },
    focusEditor() {
      this.$nextTick(() => this.$refs.composer?.focus());
    },
    sendSegments(segments) {
      const message = JSON.stringify(segments);
      if (this.context === "reply") {
        this.replyMessage(this.entityId, message);
      } else {
        this.sendMessage(message, this.context, this.entityId);
      }
      this.saveDraft("");
      this.$emit("message-sent");
      return Promise.resolve();
    },
    handleEditorFocus() {
      this.$emit("editor:focus");
    },
  },
};
</script>
